import styles from '../pages/OutreachPage.module.css';


/**
 * Tiny refresh control for outreach tables. Shows a circular-arrow glyph
 * + spins while the underlying query is fetching. Disabled while in
 * flight to prevent click-spam.
 *
 * Usage:
 *   const q = useCampaignRecipients(...);
 *   <RefreshButton onClick={() => q.refetch()} isFetching={q.isFetching} />
 *
 * For tables that already auto-refresh on a `refetchInterval` cadence,
 * the button is the manual escape hatch when the user wants the latest
 * state immediately (e.g. just launched a campaign and is watching the
 * recipients flip from `pending` to `in_progress`).
 */
export function RefreshButton({
  onClick,
  isFetching,
  title,
}: {
  onClick: () => void;
  isFetching: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      className={styles.button}
      onClick={onClick}
      disabled={isFetching}
      title={title || 'Refresh'}
      style={{
        fontSize: 13,
        padding: '4px 10px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        opacity: isFetching ? 0.6 : 1,
      }}
      aria-label="Refresh"
    >
      <span
        style={{
          display: 'inline-block',
          fontSize: 14,
          lineHeight: 1,
          animation: isFetching ? 'outreachRefreshSpin 0.8s linear infinite' : undefined,
        }}
      >
        ↻
      </span>
      <span>{isFetching ? 'Refreshing…' : 'Refresh'}</span>
      <style>
        {`@keyframes outreachRefreshSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }`}
      </style>
    </button>
  );
}
